/**
 * Zaman kuralları (AGENTS.md §3.3): saklama UTC `timestamptz`, görüntüleme `Europe/Istanbul`.
 * Dönüşümler sabit "+03:00" varsayımıyla DEĞİL, IANA saat dilimi verisiyle yapılır; böylece 2016 öncesi
 * yaz saati uygulanan tarihler ve olası gelecekteki mevzuat değişiklikleri doğru işlenir.
 */
export const DISPLAY_TIME_ZONE = "Europe/Istanbul";
const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;
const partsFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: DISPLAY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
});
function zonedParts(instant) {
    const lookup = {};
    for (const part of partsFormatter.formatToParts(instant)) {
        if (part.type !== "literal")
            lookup[part.type] = Number(part.value);
    }
    return {
        year: lookup.year,
        month: lookup.month,
        day: lookup.day,
        hour: lookup.hour,
        minute: lookup.minute,
        second: lookup.second,
    };
}
const pad = (value, length = 2) => String(value).padStart(length, "0");
export function isValidLocalDate(value) {
    const match = LOCAL_DATE_PATTERN.exec(value);
    if (!match)
        return false;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const probe = new Date(Date.UTC(year, month - 1, day));
    return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}
export function localDate(value) {
    if (!isValidLocalDate(value))
        throw new RangeError(`Geçersiz tarih: "${value}" (beklenen: YYYY-MM-DD)`);
    return value;
}
/** Bir anın İstanbul'daki takvim günü. */
export function toIstanbulDate(instant) {
    const parts = zonedParts(instant);
    return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}`;
}
/** İstanbul saat diliminin verilen andaki UTC farkı (dakika). Bugün için +180. */
export function istanbulOffsetMinutes(instant) {
    const parts = zonedParts(instant);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000);
}
/** İstanbul yerel tarih + saatini UTC ana çevirir. `time`: "HH:mm" veya "HH:mm:ss". */
export function istanbulDateTimeToUtc(date, time = "00:00") {
    const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
    if (!match)
        throw new RangeError(`Geçersiz saat: "${time}"`);
    const [year, month, day] = date.split("-").map(Number);
    const guess = Date.UTC(year, month - 1, day, Number(match[1]), Number(match[2]), Number(match[3] ?? 0));
    // Yerel saat → UTC: önce tahmini an için farkı bul, sonra düzeltilmiş an için yeniden doğrula (DST geçişleri).
    const firstPass = guess - istanbulOffsetMinutes(new Date(guess)) * 60_000;
    return new Date(guess - istanbulOffsetMinutes(new Date(firstPass)) * 60_000);
}
/** Bir İstanbul takvim gününün [başlangıç, bitiş) UTC aralığı. Sorgularda `>= start AND < end` olarak kullanılır. */
export function istanbulDayRange(date) {
    return { start: istanbulDateTimeToUtc(date), end: istanbulDateTimeToUtc(addDays(date, 1)) };
}
export function addDays(date, days) {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day) + days * MS_PER_DAY).toISOString().slice(0, 10);
}
/** Ay ekleme; ay sonu taşmasında hedef ayın son günü (31.01 + 1 ay = 28/29.02). Uyum geçerliliği, abonelik dönemi. */
export function addMonths(date, months) {
    const [year, month, day] = date.split("-").map(Number);
    const target = new Date(Date.UTC(year, month - 1 + months, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, lastDay));
    return target.toISOString().slice(0, 10);
}
/** `to − from` gün farkı (vade hesabı, yaşlandırma). */
export function diffDays(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}
/** 1 = Pazartesi … 7 = Pazar (ISO 8601). */
export function isoWeekday(date) {
    const day = new Date(`${date}T00:00:00Z`).getUTCDay();
    return day === 0 ? 7 : day;
}
export function isWeekend(date) {
    return isoWeekday(date) >= 6;
}
/**
 * Vade hafta sonuna veya verilen resmî tatillere denk geliyorsa izleyen ilk iş gününe kaydırır.
 * Resmî tatil listesi koda gömülmez; çağıran taraf geçerlilik tarihli yapılandırmadan sağlar (AGENTS.md §3.12).
 */
export function nextWorkingDay(date, holidays = new Set()) {
    let current = date;
    while (isWeekend(current) || holidays.has(current))
        current = addDays(current, 1);
    return current;
}
/**
 * Varsayılan iş günü (business_date) hesabı.
 * İş günü gece yarısında değil, gün sonu kapanışında değişir: kesim saati 06:00 ise 02:30'daki satış ÖNCEKİ güne aittir.
 * Açık bir vardiya varsa iş günü vardiyadan gelir; bu fonksiyon yalnızca vardiya bağlamı olmayan belgeler içindir.
 */
export function businessDateFor(instant, dayStartsAt = "00:00") {
    const match = /^(\d{2}):(\d{2})$/.exec(dayStartsAt);
    if (!match)
        throw new RangeError(`Geçersiz gün başlangıç saati: "${dayStartsAt}"`);
    const cutoffMinutes = Number(match[1]) * 60 + Number(match[2]);
    const parts = zonedParts(instant);
    const calendarDay = toIstanbulDate(instant);
    return parts.hour * 60 + parts.minute < cutoffMinutes ? addDays(calendarDay, -1) : calendarDay;
}
/** "18.09.2026" */
export function formatTrDate(value) {
    const date = typeof value === "string" ? value : toIstanbulDate(value);
    const [year, month, day] = date.split("-");
    return `${day}.${month}.${year}`;
}
/** "18.09.2026 14:05" (saniye istenirse "18.09.2026 14:05:09") */
export function formatTrDateTime(instant, options = {}) {
    const parts = zonedParts(instant);
    const time = `${pad(parts.hour)}:${pad(parts.minute)}${options.seconds ? `:${pad(parts.second)}` : ""}`;
    return `${formatTrDate(toIstanbulDate(instant))} ${time}`;
}
/** "18.09.2026" → "2026-09-18". Geçersizse null. */
export function parseTrDate(value) {
    const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value.trim());
    if (!match)
        return null;
    const candidate = `${match[3]}-${pad(Number(match[2]))}-${pad(Number(match[1]))}`;
    return isValidLocalDate(candidate) ? candidate : null;
}
//# sourceMappingURL=istanbul.js.map